import { Link } from "@tanstack/react-router";
import { SiteFooter } from "@/components/SiteFooter";
import { ShareButton } from "@/components/ShareButton";
import { useEffect, useState } from "react";
import { GAMES, relatedGames, type GameMeta } from "@/data/games";
import { PACKS, QUOTES, dailySeed, seededPick, quotesByPack, type PackId } from "@/data/quotes";
import {
  SCRAMBLE_LEVELS,
  loadBest,
  loadStreak,
  loadStars,
  loadBloomLevel,
} from "@/lib/game-utils";
import { TOTAL_BOARDS } from "@/data/word-bloom";

interface Props {
  game: GameMeta;
}

export function GameHub({ game }: Props) {
  const [best, setBest] = useState(0);
  const [streak, setStreak] = useState(0);
  const [stars, setStars] = useState<number[]>([0, 0, 0, 0, 0]);
  const [bloomLevel, setBloomLevel] = useState(1);
  const [pack, setPack] = useState<PackId | "all">("all");
  const isPuzzle = game.type === "puzzle";
  const isWordle = game.type === "wordle";
  const isLetters = game.type === "letters";

  useEffect(() => {
    setBest(loadBest(game.slug));
    setStreak(loadStreak(game.slug));
    if (isPuzzle) setStars(SCRAMBLE_LEVELS.map((l) => loadStars(l.id)));
    if (isLetters) setBloomLevel(loadBloomLevel());
  }, [game.slug, isPuzzle, isLetters]);

  // Daily challenge — same quote for everyone today.
  const daily = seededPick(dailySeed() + game.slug.length, QUOTES);
  const featured = quotesByPack("all").slice(0, 3);

  // Share card text: game name, Quote of the Day, credit, then the play link.
  const shareText = [
    game.name,
    "",
    `"${daily.text}"`,
    "",
    `— ${daily.author}${daily.era ? ` · ${daily.era}` : ""}`,
    "",
    `Play at https://mrbquotes.com/games/${game.slug}`,
  ].join("\n");

  return (
    <div className="min-h-screen">
      {/* Top nav */}
      <header className="border-b border-border/60">
        <div className="mx-auto flex max-w-6xl items-center justify-between px-4 py-5">
          <Link to="/" className="flex items-center gap-2 group">
            <span className="font-display text-2xl text-gold">Mr. B</span>
            <span className="text-[10px] uppercase tracking-[0.3em] text-muted-foreground group-hover:text-foreground transition">Quotes · Games</span>
          </Link>
          <nav className="flex items-center gap-3 text-sm">
            <Link to="/games" className="text-muted-foreground hover:text-foreground transition">All games</Link>
            <Link to="/live" className="rounded-full border border-gold/40 bg-gold/10 px-3 py-1.5 text-xs text-gold uppercase tracking-widest">Live Mode</Link>
          </nav>
        </div>
      </header>

      {/* Masthead */}
      <section className="relative overflow-hidden border-b border-border/60">
        <div className="absolute -top-32 -right-32 h-96 w-96 rounded-full bg-gradient-gold opacity-10 blur-3xl" />
        <div className="mx-auto max-w-6xl px-4 pt-14 pb-12 sm:pt-20 sm:pb-16 relative">
          <Link to="/games" className="text-xs text-muted-foreground hover:text-gold transition">← Back to the collection</Link>
          <div className="mt-4 grid gap-10 lg:grid-cols-[1.3fr_1fr] lg:items-end">
            <div className="animate-reveal">
              <div className="flex items-baseline gap-4">
                <span className="font-display text-7xl sm:text-9xl text-gold/30 leading-none">{game.numeral}</span>
                <div className="text-[10px] uppercase tracking-[0.3em] text-muted-foreground">Game {game.numeral} · {game.type === "trivia" ? "Trivia" : game.type === "puzzle" ? "Puzzle" : game.type === "letters" ? "Letter Puzzle" : "Word Puzzle"}</div>
              </div>
              <h1 className="mt-3 font-display text-5xl sm:text-7xl leading-[0.95]">{game.name}</h1>
              <p className="mt-4 text-lg text-muted-foreground italic font-display">{game.tagline}</p>
              <p className="mt-4 max-w-xl text-base text-muted-foreground leading-relaxed">{game.description}</p>
              <div className="mt-8 flex flex-wrap gap-3">
                <Link
                  to="/games/$slug/play"
                  params={{ slug: game.slug }}
                  search={isWordle ? { mode: "endless" } : isLetters ? { level: bloomLevel } : isPuzzle ? { level: 1 } : pack === "all" ? {} : { pack }}
                  className="rounded-full bg-gradient-gold px-6 py-3 text-sm font-semibold text-ink shadow-gold hover:opacity-90 transition"
                >
                  {isLetters && bloomLevel > 1 ? `Continue level ${bloomLevel} →` : "Play now →"}
                </Link>
                <Link
                  to="/games/$slug/play"
                  params={{ slug: game.slug }}
                  search={isWordle ? { mode: "endless", live: "1" } : isLetters ? { level: bloomLevel, live: "1" } : isPuzzle ? { level: 1, live: "1" } : { live: "1" }}
                  className="rounded-full border border-gold/40 bg-gold/5 text-gold px-6 py-3 text-sm font-medium hover:bg-gold/10 transition"
                >
                  ● Live mode
                </Link>
              </div>
            </div>

            {/* Daily challenge card */}
            <aside className="rounded-2xl border border-border bg-card/80 p-6 sm:p-8 shadow-elegant grain">
              <div className="flex items-center justify-between">
                <div className="text-[10px] uppercase tracking-[0.3em] text-gold">Daily Challenge</div>
                <span className="text-[10px] uppercase tracking-widest text-muted-foreground">{new Date().toLocaleDateString(undefined, { month: "short", day: "numeric" })}</span>
              </div>
              <div className="quote-mark mt-3" />
              <p className="font-display text-xl sm:text-2xl leading-snug">{daily.text}</p>
              <div className="mt-4 text-sm text-gold">— {daily.author}{daily.era ? <span className="text-muted-foreground"> · {daily.era}</span> : null}</div>
              <p className="mt-3 text-xs text-muted-foreground">A new quote appears every day. Play through it in any game mode.</p>
              <div className="mt-5">
                <ShareButton
                  text={shareText}
                  title={`${game.name} — Mr. B Quotes`}
                  label="Share today's quote"
                  className="inline-flex items-center gap-2 rounded-full border border-gold/40 bg-gold/5 px-5 py-2.5 text-xs font-medium uppercase tracking-widest text-gold hover:bg-gold/10 transition"
                />
              </div>
            </aside>
          </div>
        </div>
      </section>

      <main className="mx-auto max-w-6xl px-4 py-14 grid gap-14">
        {/* How to play */}
        <section>
          <h2 className="font-display text-3xl sm:text-4xl">How to play</h2>
          <div className="mt-6 grid gap-4 sm:grid-cols-3">
            {game.steps.map((step, i) => (
              <div key={i} className="rounded-2xl border border-border bg-card/60 p-6">
                <div className="font-display text-4xl text-gold/40">{String(i + 1).padStart(2, "0")}</div>
                <h3 className="mt-3 font-display text-xl text-foreground">{step.title}</h3>
                <p className="mt-2 text-sm text-muted-foreground leading-relaxed">{step.body}</p>
              </div>
            ))}
          </div>
        </section>

        {/* Mode picker — letters / wordle / puzzle / trivia */}
        {isLetters ? (
          <section>
            <div className="flex items-end justify-between gap-6">
              <div>
                <div className="text-[10px] uppercase tracking-[0.3em] text-muted-foreground">Pick a level</div>
                <h2 className="mt-1 font-display text-3xl sm:text-4xl">Unlimited levels</h2>
              </div>
              <p className="hidden sm:block max-w-sm text-sm text-muted-foreground">
                Levels never run out — {TOTAL_BOARDS} hand-checked letter sets, reshuffled endlessly. Your progress saves on this device.
              </p>
            </div>
            <div className="mt-6 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
              <Link
                to="/games/$slug/play"
                params={{ slug: game.slug }}
                search={{ level: bloomLevel }}
                className="group rounded-2xl border-2 border-gold/60 bg-gold/5 p-6 hover:border-gold transition"
              >
                <div className="flex items-baseline justify-between">
                  <span className="font-display text-3xl text-gold">{bloomLevel > 1 ? "Continue" : "Start"}</span>
                  <span className="text-[10px] uppercase tracking-widest text-gold">Level {bloomLevel}</span>
                </div>
                <p className="mt-2 text-sm text-muted-foreground">
                  {bloomLevel > 1 ? "Pick up exactly where you left off." : "Five letters, a handful of words. A gentle start."}
                </p>
                <div className="mt-4 text-xs text-gold uppercase tracking-widest">Play level {bloomLevel} →</div>
              </Link>
              {[
                { level: 1, name: "Level 1", blurb: "5 letters · warm-up boards." },
                { level: 9, name: "Level 9", blurb: "6 letters · more words per board." },
                { level: 21, name: "Level 21", blurb: "7 letters · pangram hunting." },
              ].map((l) => (
                <Link
                  key={l.level}
                  to="/games/$slug/play"
                  params={{ slug: game.slug }}
                  search={{ level: l.level }}
                  className="group rounded-2xl border border-border bg-card p-6 hover:border-gold transition"
                >
                  <div className="flex items-baseline justify-between">
                    <span className="font-display text-3xl text-gold/70 group-hover:text-gold">{l.level}</span>
                    <span className="text-[10px] uppercase tracking-widest text-muted-foreground">{l.name}</span>
                  </div>
                  <p className="mt-2 text-sm text-muted-foreground">{l.blurb}</p>
                  <div className="mt-4 text-xs text-gold uppercase tracking-widest">Jump in →</div>
                </Link>
              ))}
            </div>
          </section>
        ) : isWordle ? (
          <section>
            <div className="flex items-end justify-between gap-6">
              <div>
                <div className="text-[10px] uppercase tracking-[0.3em] text-muted-foreground">Pick a mode</div>
                <h2 className="mt-1 font-display text-3xl sm:text-4xl">Daily or Endless</h2>
              </div>
              <p className="hidden sm:block max-w-sm text-sm text-muted-foreground">Daily is the same quote for everyone, resets at midnight UTC. Endless plays five fresh puzzles back-to-back.</p>
            </div>
            <div className="mt-6 grid gap-3 sm:grid-cols-2">
              <Link
                to="/games/$slug/play"
                params={{ slug: game.slug }}
                search={{ mode: "daily" }}
                className="group rounded-2xl border-2 border-gold/60 bg-gold/5 p-6 hover:border-gold transition"
              >
                <div className="flex items-baseline justify-between">
                  <span className="font-display text-3xl text-gold">Daily Quote</span>
                  <span className="text-[10px] uppercase tracking-widest text-gold">Shareable</span>
                </div>
                <p className="mt-2 text-sm text-muted-foreground">One puzzle, same for everyone today. Solve it once. Share the emoji grid.</p>
                <div className="mt-4 text-xs text-gold uppercase tracking-widest">Play today's quote →</div>
              </Link>
              <Link
                to="/games/$slug/play"
                params={{ slug: game.slug }}
                search={{ mode: "endless" }}
                className="group rounded-2xl border border-border bg-card p-6 hover:border-gold transition"
              >
                <div className="flex items-baseline justify-between">
                  <span className="font-display text-3xl text-gold/70 group-hover:text-gold">Endless Pack</span>
                  <span className="text-[10px] uppercase tracking-widest text-muted-foreground">5 puzzles</span>
                </div>
                <p className="mt-2 text-sm text-muted-foreground">Five quotes back-to-back. Total score, shareable run.</p>
                <div className="mt-4 text-xs text-gold uppercase tracking-widest">Start endless run →</div>
              </Link>
            </div>
          </section>
        ) : isPuzzle ? (
          <section>
            <div className="flex items-end justify-between gap-6">
              <div>
                <div className="text-[10px] uppercase tracking-[0.3em] text-muted-foreground">Select level</div>
                <h2 className="mt-1 font-display text-3xl sm:text-4xl">Five levels of difficulty</h2>
              </div>
              <p className="hidden sm:block max-w-sm text-sm text-muted-foreground">Each level unlocks longer quotes and tighter timers. Stars persist on this device.</p>
            </div>
            <div className="mt-6 grid gap-3 sm:grid-cols-2 lg:grid-cols-5">
              {SCRAMBLE_LEVELS.map((lvl, i) => {
                const earnedStars = stars[i] ?? 0;
                return (
                  <Link
                    key={lvl.id}
                    to="/games/$slug/play"
                    params={{ slug: game.slug }}
                    search={{ level: lvl.id }}
                    className="group relative rounded-2xl border border-border bg-card p-5 hover:border-gold hover:shadow-elegant transition"
                  >
                    <div className="flex items-baseline justify-between">
                      <span className="font-display text-3xl text-gold/60 group-hover:text-gold">{lvl.id}</span>
                      <div className="flex gap-0.5">
                        {[1, 2, 3].map((s) => (
                          <span key={s} className={s <= earnedStars ? "text-gold" : "text-border"}>★</span>
                        ))}
                      </div>
                    </div>
                    <div className="mt-3 font-display text-lg">{lvl.name}</div>
                    <div className="text-xs text-muted-foreground">{lvl.subtitle}</div>
                    <div className="mt-3 text-[10px] uppercase tracking-widest text-muted-foreground">
                      {lvl.min === 0 ? "≤" : lvl.min + "–"}{lvl.max > 100 ? "20+" : lvl.max} words · {lvl.timeLimit}s
                    </div>
                  </Link>
                );
              })}
            </div>
          </section>
        ) : (
          <section>
            <div className="flex items-end justify-between gap-6">
              <div>
                <div className="text-[10px] uppercase tracking-[0.3em] text-muted-foreground">Choose a pack</div>
                <h2 className="mt-1 font-display text-3xl sm:text-4xl">Play by theme</h2>
              </div>
              <p className="hidden sm:block max-w-sm text-sm text-muted-foreground">Filter the quote library by era or movement. Or play the full collection.</p>
            </div>
            <div className="mt-6 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
              <PackCard active={pack === "all"} onClick={() => setPack("all")} name="All Quotes" blurb="Every quote in the library." count={QUOTES.length} />
              {PACKS.map((p) => (
                <PackCard
                  key={p.id}
                  active={pack === p.id}
                  onClick={() => setPack(p.id)}
                  name={p.name}
                  blurb={p.blurb}
                  count={quotesByPack(p.id).length}
                />
              ))}
            </div>
            <div className="mt-6">
              <Link
                to="/games/$slug/play"
                params={{ slug: game.slug }}
                search={pack === "all" ? {} : { pack }}
                className="inline-flex items-center gap-2 rounded-full bg-gradient-gold px-6 py-3 text-sm font-semibold text-ink"
              >
                Start with {pack === "all" ? "all quotes" : PACKS.find((p) => p.id === pack)?.name} →
              </Link>
            </div>
          </section>
        )}

        {/* Stats */}
        <section className="rounded-2xl border border-border bg-card/60 p-6 sm:p-8">
          <div className="text-[10px] uppercase tracking-[0.3em] text-muted-foreground">Your record</div>
          <div className="mt-3 grid grid-cols-2 sm:grid-cols-4 gap-6">
            <Stat n={String(best)} l={isWordle ? "Best run" : isPuzzle || isLetters ? "Best score" : "Best score / 10"} />
            <Stat n={isLetters ? String(bloomLevel) : String(streak)} l={isLetters ? "Level reached" : isWordle ? "Daily streak" : "Longest streak"} />
            <Stat n={isLetters ? "∞" : isWordle ? "6" : isPuzzle ? `${stars.reduce((a, b) => a + b, 0)}/15` : "10"} l={isLetters ? "Levels" : isWordle ? "Guesses / puzzle" : isPuzzle ? "Stars earned" : "Rounds / session"} />
            <Stat n={String(QUOTES.length)} l="Curated quotes" />
          </div>
        </section>

        {/* Featured quotes — SEO body content */}
        <section>
          <h2 className="font-display text-3xl sm:text-4xl">Sample quotes from the library</h2>
          <p className="mt-2 text-sm text-muted-foreground max-w-2xl">A small sample of the {QUOTES.length} widely-attributed, sourced quotations powering {game.name}.</p>
          <div className="mt-6 grid gap-4 sm:grid-cols-3">
            {featured.map((q) => (
              <article key={q.id} className="rounded-2xl border border-border bg-card p-6 grain">
                <div className="quote-mark" />
                <p className="font-display text-lg leading-snug">{q.text}</p>
                <div className="mt-4 pt-4 border-t border-border text-sm">
                  <div className="text-gold">— {q.author}</div>
                  {q.source && <div className="text-xs text-muted-foreground mt-1">{q.source}</div>}
                </div>
              </article>
            ))}
          </div>
        </section>

        {/* About — long-form SEO copy */}
        <section className="grid lg:grid-cols-[2fr_1fr] gap-10">
          <div>
            <h2 className="font-display text-3xl sm:text-4xl">About {game.name}</h2>
            <div className="mt-4 space-y-4 text-muted-foreground leading-relaxed max-w-2xl">
              {game.about.map((p, i) => <p key={i}>{p}</p>)}
            </div>
          </div>
          <aside className="rounded-2xl border border-border bg-card/60 p-6 h-fit">
            <h3 className="font-display text-xl text-gold">Stream-ready</h3>
            <p className="mt-2 text-sm text-muted-foreground">Open this game in Live Mode for OBS-friendly typography, an on-screen timer and A/B/C/D answer codes your chat can vote on in real time.</p>
            <Link
              to="/games/$slug/play"
              params={{ slug: game.slug }}
              search={isWordle ? { mode: "endless", live: "1" } : isPuzzle ? { level: 1, live: "1" } : { live: "1" }}
              className="mt-4 inline-flex items-center gap-2 rounded-full border border-gold/40 bg-gold/5 px-4 py-2 text-xs text-gold uppercase tracking-widest"
            >
              ● Open in Live Mode
            </Link>
          </aside>
        </section>

        {/* FAQ */}
        <section>
          <h2 className="font-display text-3xl sm:text-4xl">Frequently asked questions</h2>
          <div className="mt-6 divide-y divide-border rounded-2xl border border-border bg-card/40">
            {game.faq.map((f, i) => (
              <details key={i} className="group p-5">
                <summary className="flex cursor-pointer items-center justify-between font-display text-lg list-none">
                  <span>{f.q}</span>
                  <span className="text-gold transition-transform group-open:rotate-45">+</span>
                </summary>
                <p className="mt-3 text-sm text-muted-foreground leading-relaxed">{f.a}</p>
              </details>
            ))}
          </div>
        </section>

        {/* Related games */}
        <section>
          <h2 className="font-display text-3xl sm:text-4xl">More quote games</h2>
          <div className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {relatedGames(game.slug).map((g) => (
              <Link
                key={g.slug}
                to="/games/$slug"
                params={{ slug: g.slug }}
                className="group rounded-2xl border border-border bg-card p-5 hover:border-gold transition"
              >
                <div className="flex items-baseline justify-between">
                  <span className="font-display text-3xl text-gold/40 group-hover:text-gold">{g.numeral}</span>
                  <span className="text-[10px] uppercase tracking-widest text-muted-foreground">{g.tagline}</span>
                </div>
                <h3 className="mt-2 font-display text-lg">{g.name}</h3>
                <p className="mt-1 text-xs text-muted-foreground">{g.description}</p>
              </Link>
            ))}
          </div>
        </section>
      </main>

      <SiteFooter />

    </div>
  );
}

function Stat({ n, l }: { n: string; l: string }) {
  return (
    <div>
      <div className="font-display text-4xl text-gold">{n}</div>
      <div className="text-xs uppercase tracking-widest text-muted-foreground mt-1">{l}</div>
    </div>
  );
}

function PackCard({ name, blurb, count, active, onClick }: { name: string; blurb: string; count: number; active: boolean; onClick: () => void }) {
  return (
    <button
      onClick={onClick}
      className={`text-left rounded-2xl border p-5 transition ${
        active ? "border-gold bg-gold/10 shadow-elegant" : "border-border bg-card hover:border-gold/50"
      }`}
    >
      <div className="flex items-center justify-between">
        <h3 className="font-display text-lg">{name}</h3>
        <span className="text-[10px] font-mono text-gold">{count}</span>
      </div>
      <p className="mt-1 text-xs text-muted-foreground leading-relaxed">{blurb}</p>
    </button>
  );
}
